// ============================================================
// THE CONDITIONS ON A PERSON'S PLATE (plan body-map S1; the bench's rulings
// of 2026-10-08). Nothing is drawn over the figure: each condition changes
// the lines themselves, on a layer of its own, so any mix of them stacks.
// Outside in:
//
//   dying (sinking, a weak heartbeat) > Haywire (the scan jammed) >
//   hallucinating (the figure warped like liquid) > the body's state
//   (paralysed icy, unconscious dim, helpless dim, unaware cool) > fatigue
//   (panting from the feet) > pinned (near misses flicker) > stunned (drifting
//   ghosts) > on fire (heat shimmer) > the lines (burning colours; dead
//   drains to grey) > per location: the senses on the head, the chest's slow
//   breath, the cords on the legs.
//
// Over them, recoloured copies of the lines shown through a moving band: the
// voice that dies (Cannot Speak), the calm glow (Unaware), the blood draining
// (Blood Loss). No lasting condition moves the figure but Fatigue (ruling).
//
// What the bench animated with SMIL is a TRACK here (engine/plate/tracks),
// rendered at its still value and moved by usePlateClock; loops are CSS
// keyframes behind the motion setting. Every condition has a still form.
// ============================================================

import { useState, type CSSProperties, type ReactNode } from 'react';
import clsx from '../../../utils/clsx';
import type { PlateState } from '../../../engine/plate/plateState';
import { regionBox, type PlateArt } from '../../../engine/plate/figureArt';
import fx from '../../../styles/PlateFx.module.css';
import plate from '../../../styles/BodyPlate.module.css';
import { tracked } from '../usePlateClock';

/** True once `on` has turned on while mounted: a moment (a death, the crew's flash, the blast) plays then only - a plate
 * that mounts already dead is simply grey. */
export function useArrived(on: boolean): boolean {
  // Re-armed whenever it goes off: a revived figure killed again drains again. (State adjusted during render.)
  const [seenOff, setSeenOff] = useState(!on);
  if (!on && !seenOff) setSeenOff(true);
  return on && seenOff;
}

/**
 * A filter's scale for its frame: 1 on the figures' (2123 x 3000 art in a 3763-wide plate), 0.5 on the goo's smaller
 * frame (the study's own numbers) - distances times it, noise frequencies divided by it.
 */
interface Scaled {
  u: string;
  k?: number;
}

const flood = (token: string) => ({ floodColor: `var(${token})` }) as CSSProperties;

/** What the conditions need of the drawing: the head's band, where the senses stop on it, and the chest. */
export interface FigureMarks {
  /** The head region's top and bottom (frame y), or null for a figure without one. */
  head: [number, number] | null;
  /** Where Blinded stops on a helmeted head, and where its collar begins (frame y), else null. */
  eyeLine: number | null;
  skullLine: number | null;
  /** The chest, where the slow breath swells from. */
  chest: readonly [number, number];
}

/** The art's view units (the bench's 800 x 1200) to the frame's. */
const VIEW_TO_FRAME = 2.5;

export function figureMarks(art: PlateArt): FigureMarks {
  const head = art.regions.head ? regionBox(art.regions.head) : null;
  return {
    head: head && [head[1], head[3]],
    eyeLine: art.eyeLine === null ? null : art.eyeLine * VIEW_TO_FRAME,
    skullLine: art.skullLine === null ? null : art.skullLine * VIEW_TO_FRAME,
    chest: art.anchors.body,
  };
}

/** A deafened helmet: the blur is cut at the skull line and the collar drawn sharp again. */
const deafCut = (s: PlateState, m: FigureMarks) => s.deaf && m.skullLine !== null;

// ── Haywire: the scan jammed ─────────────────────────────────────

/** Per severity (1 Minor Disruption .. 4 Total Shutdown): the bursts' strength, the blocks dropped, the split. */
const HAYWIRE = [
  null,
  { burst: [0, 0, 0, 0, 0, 0, 40, 0], drop: '1 1 1 1 1 1 1 1', split: 10 },
  { burst: [0, 60, 0, 0, 90, 0, 70, 0], drop: '1 1 0 1 1 1 1 1', split: 22 },
  { burst: [110, 80, 140, 0, 120, 90, 150, 100], drop: '1 0 0 1 0 1 1 0', split: 36 },
  { burst: [160, 200, 140, 180, 220, 150, 190, 170], drop: '0 1 0 0 1 0 0 0', split: 50 },
];

/** Horizontal bands of the image shoved sideways, blocks of it dropped, a colour split - in bursts. */
function HaywireFilter({ u, level, k = 1 }: Scaled & { level: number }) {
  const h = HAYWIRE[level]!;
  const seed = { attr: 'seed', values: [2, 7, 11, 4, 9, 13, 5, 8], dur: 0.9, mode: 'discrete' as const };
  const shoves = h.burst.map((b) => b * k);
  const burst = { attr: 'scale', values: shoves, dur: 1.8, mode: 'discrete' as const, still: Math.max(...shoves) };
  return (
    <filter id={`${u}-hw`} x="-15%" y="-5%" width="130%" height="110%">
      <feTurbulence
        type="turbulence"
        baseFrequency={`0.00001 ${0.008 / k}`}
        numOctaves={1}
        result="n"
        {...tracked([seed])}
      />
      <feComponentTransfer in="n" result="q">
        <feFuncR type="discrete" tableValues=".5 .9 .5 .1 .5 .75 .5 .3" />
        <feFuncG type="discrete" tableValues=".5" />
        <feFuncB type="discrete" tableValues={h.drop} />
        <feFuncA type="discrete" tableValues="1" />
      </feComponentTransfer>
      <feDisplacementMap
        in="SourceGraphic"
        in2="q"
        xChannelSelector="R"
        yChannelSelector="G"
        result="d"
        {...tracked([burst])}
      />
      <feColorMatrix in="q" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 1 0 0" result="keep" />
      <feComposite in="d" in2="keep" operator="in" result="kept" />
      <SplitColour from="kept" token="--plate-haywire-a" opacity={0.55} dx={h.split * k} result="a" />
      <SplitColour from="kept" token="--plate-haywire-b" opacity={0.45} dx={-h.split * k} result="b" />
      <feMerge>
        <feMergeNode in="a" />
        <feMergeNode in="b" />
        <feMergeNode in="kept" />
      </feMerge>
    </filter>
  );
}

/** One side of a colour split: the shape flooded with a token's colour, shifted sideways. */
function SplitColour({
  from,
  token,
  opacity,
  dx,
  result,
}: {
  from: string;
  token: string;
  opacity: number;
  dx: number;
  result: string;
}) {
  return (
    <>
      <feFlood style={flood(token)} floodOpacity={opacity} result={`${result}c`} />
      <feComposite in={`${result}c`} in2={from} operator="in" result={`${result}s`} />
      <feOffset in={`${result}s`} dx={dx} dy={0} result={result} />
    </>
  );
}

// ── Hallucinating: the figure warped like liquid ─────────────────

/** Per band (1 faint .. 6 lost): the warp, its noise's frequency, the wandering copies, the hue's turn (s). */
const HALLUC = [
  null,
  { warp: 14, freq: [0.004, 0.006, 0.004], copies: 0, hue: 0 },
  { warp: 32, freq: [0.003, 0.006, 0.003], copies: 1, hue: 0 },
  { warp: 55, freq: [0.003, 0.007, 0.003], copies: 1, hue: 14 },
  { warp: 90, freq: [0.0025, 0.008, 0.0025], copies: 2, hue: 8 },
  { warp: 130, freq: [0.002, 0.009, 0.002], copies: 3, hue: 5 },
  { warp: 190, freq: [0.002, 0.01, 0.002], copies: 3, hue: 4 },
];

function HallucFilter({ u, band, k = 1 }: Scaled & { band: number }) {
  const h = HALLUC[band]!;
  const src = h.hue ? 'd2' : 'd';
  const copies = Array.from({ length: h.copies }, (_, i) => i);
  return (
    <filter id={`${u}-hl`} x="-30%" y="-15%" width="160%" height="130%">
      <feTurbulence
        type="fractalNoise"
        numOctaves={2}
        seed={5}
        result="n"
        {...tracked([{ attr: 'baseFrequency', values: h.freq.map((f) => f / k), dur: 6 }])}
      />
      <feDisplacementMap
        in="SourceGraphic"
        in2="n"
        scale={h.warp * k}
        xChannelSelector="R"
        yChannelSelector="G"
        result="d"
      />
      {h.hue > 0 && (
        <feColorMatrix
          in="d"
          type="hueRotate"
          result="d2"
          {...tracked([{ attr: 'values', values: [0, 360], dur: h.hue }])}
        />
      )}
      {copies.map((i) => (
        <WanderingCopy key={i} i={i} from={src} k={k} />
      ))}
      <feMerge>
        {copies.map((i) => (
          <feMergeNode key={i} in={`f${i}`} />
        ))}
        <feMergeNode in={src} />
      </feMerge>
    </filter>
  );
}

/** A faint copy of the figure that drifts away and back; held aside, half out, with motion off. */
function WanderingCopy({ i, from, k }: { i: number; from: string; k: number }) {
  const r = (70 + 50 * i) * k;
  const dur = 5 + 2 * i;
  const dx = { attr: 'dx', values: [0, r, -r * 0.6, 0], dur, still: r * 0.5 };
  const dy = { attr: 'dy', values: [0, -r * 0.5, r * 0.4, 0], dur: dur + 1.3, still: -r * 0.25 };
  return (
    <>
      <feOffset in={from} result={`c${i}`} {...tracked([dx, dy])} />
      <feComponentTransfer in={`c${i}`} result={`f${i}`}>
        <feFuncA type="linear" slope={0.45 - 0.1 * i} />
      </feComponentTransfer>
    </>
  );
}

// ── Stunned, on fire, deafened ───────────────────────────────────

/** Two ghosts of the figure drifting off it - one cold, one pale; held aside with motion off. */
function StunFilter({ u, k = 1 }: Scaled) {
  const at = (values: number[], still: number) => ({ values: values.map((v) => v * k), still: still * k });
  const cold = { attr: 'dx', ...at([0, 46, -30, 18, 0], 30), dur: 1.6 };
  const paleX = { attr: 'dx', ...at([0, -34, 22, -12, 0], -22), dur: 1.6 };
  const paleY = { attr: 'dy', ...at([0, 10, -8, 0], 6), dur: 2.3 };
  return (
    <filter id={`${u}-stun`} x="-10%" y="-10%" width="120%" height="120%">
      <feFlood style={flood('--plate-stun')} floodOpacity={0.75} result="bc" />
      <feComposite in="bc" in2="SourceAlpha" operator="in" result="b" />
      <feOffset in="b" result="bo" {...tracked([cold])} />
      <feFlood style={flood('--plate-ghost')} floodOpacity={0.45} result="wc" />
      <feComposite in="wc" in2="SourceAlpha" operator="in" result="w" />
      <feOffset in="w" result="wo" {...tracked([paleX, paleY])} />
      <feMerge>
        <feMergeNode in="bo" />
        <feMergeNode in="wo" />
        <feMergeNode in="SourceGraphic" />
      </feMerge>
    </filter>
  );
}

/** The heat shimmer over a burning figure. */
function HeatFilter({ u, k = 1 }: Scaled) {
  const seed = { attr: 'seed', values: [1, 2, 3, 4, 5, 6, 7, 8], dur: 0.8, mode: 'discrete' as const };
  return (
    <filter id={`${u}-heat`} x="-10%" y="-10%" width="120%" height="120%">
      <feTurbulence type="fractalNoise" baseFrequency="0.006 0.02" numOctaves={2} result="n" {...tracked([seed])} />
      <feDisplacementMap in="SourceGraphic" in2="n" scale={46 * k} xChannelSelector="R" yChannelSelector="G" />
    </filter>
  );
}

// ── The head and the legs: masks ─────────────────────────────────

/** A mask's region: well beyond any plate's frame, so nothing is cut at its edge. */
export const MASK_RECT = { x: -2000, y: -2000, width: 6200, height: 7000 };

/**
 * The head's mask: Blinded darkens the head down to the figure's eye line (the whole head where it has none); a
 * deafened helmet's blur is cut at its skull line (its collar is drawn sharp again over it).
 */
function HeadMask({ u, s, m }: { u: string; s: PlateState; m: FigureMarks }) {
  if (!m.head || !(s.blind || deafCut(s, m))) return null;
  const [y0, y1] = m.head;
  const at = (y: number) => Math.min(1, Math.max(0, (y - y0) / (y1 - y0)));
  const eye = m.eyeLine === null ? 1 : at(m.eyeLine);
  const dark = s.blind ? 0.04 : 1;
  const cut = deafCut(s, m) ? at(m.skullLine!) : null;
  const stops = [
    [0, dark],
    [eye, dark],
    [eye + 0.06, 1],
    ...(cut === null
      ? [[1, 1]]
      : [
          [cut, 1],
          [cut, 0],
          [1, 0],
        ]),
  ];
  return (
    <>
      <linearGradient id={`${u}-hg`} gradientUnits="userSpaceOnUse" x1={0} y1={y0} x2={0} y2={y1}>
        {stops.map(([o, a], i) => (
          <stop key={i} offset={Math.min(1, o)} stopColor="white" stopOpacity={a} />
        ))}
      </linearGradient>
      <mask id={`${u}-hm`} maskUnits="userSpaceOnUse" {...MASK_RECT}>
        <rect {...MASK_RECT} fill={`url(#${u}-hg)`} />
      </mask>
      {cut !== null && (
        <clipPath id={`${u}-collar`}>
          <rect x={-500} y={m.skullLine!} width={3200} height={4000} />
        </clipPath>
      )}
    </>
  );
}

/** Diagonal cords across the legs' lines (a striped mask). */
const CordMask = ({ u }: { u: string }) => <StripeMask id={`${u}-cm`} size={170} band={40} angle={28} />;

/** A mask of diagonal stripes: `band` of every `size` hidden, at `angle` degrees - cords, a broken gun. */
export function StripeMask({ id, size, band, angle }: { id: string; size: number; band: number; angle: number }) {
  return (
    <>
      <pattern
        id={`${id}-p`}
        width={size}
        height={size}
        patternUnits="userSpaceOnUse"
        patternTransform={`rotate(${angle})`}
      >
        <rect width={size} height={size} fill="white" />
        <rect width={size} height={band} fill="black" />
      </pattern>
      <mask id={id} maskUnits="userSpaceOnUse" {...MASK_RECT}>
        <rect {...MASK_RECT} fill={`url(#${id}-p)`} />
      </mask>
    </>
  );
}

// ── The moving bands the overlays show through ───────────────────
// A clip's rect, not a gradient mask: Chrome caches a mask's paint and never sees its gradient move (gotcha).

/** Cannot Speak: a band of light climbing the neck and dying at the chin, again and again; held at the chin. */
function VoiceBand({ u, head: [y0, y1] }: { u: string; head: [number, number] }) {
  const h = y1 - y0;
  const band = 0.14 * h;
  const at = (o: number) => y0 + o * h - band / 2;
  const y = { attr: 'y', values: [at(1), at(0.62), at(1)], dur: 2.6, mode: 'ease' as const, still: at(0.62) };
  return (
    <clipPath id={`${u}-voice`}>
      <rect x={-500} width={3200} height={band} {...tracked([y])} />
    </clipPath>
  );
}

/** Unaware: a slow, even glow passing down the figure; held across the chest. */
export function CalmBand({ u }: { u: string }) {
  const y = { attr: 'y', values: [-600, 2970], dur: 8, still: 1200 };
  return (
    <clipPath id={`${u}-calm`}>
      <rect x={-500} width={3200} height={672} {...tracked([y])} />
    </clipPath>
  );
}

/** Blood Loss: the grey falls from the top, slowly - or fast and deep when severe; held deep. */
function BloodBand({ u, level }: { u: string; level: number }) {
  const [lo, hi, dur] = level === 2 ? [0.35, 0.92, 3.5] : [0.12, 0.55, 7];
  const height = { attr: 'height', values: [500 + lo * 3000, 500 + hi * 3000], dur, still: 500 + hi * 3000 };
  return (
    <clipPath id={`${u}-blood`}>
      <rect x={-500} y={-500} width={3200} {...tracked([height])} />
    </clipPath>
  );
}

/** The filters of the conditions that wrap a whole body (a crowd's, where the members carry no per-location ones). */
export function WholeBodyDefs({ u, s, k = 1, deafBlur = 26 * k }: Scaled & { s: PlateState; deafBlur?: number }) {
  return (
    <>
      {s.haywire > 0 && <HaywireFilter u={u} level={s.haywire} k={k} />}
      {s.halluc > 0 && <HallucFilter u={u} band={s.halluc} k={k} />}
      {s.stun && <StunFilter u={u} k={k} />}
      {s.fire && <HeatFilter u={u} k={k} />}
      {s.deaf && (
        <filter id={`${u}-deaf`} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation={deafBlur} />
        </filter>
      )}
    </>
  );
}

/** Every def the person conditions in play need: the whole body's filters, masks and moving bands. */
export function ConditionDefs({ u, s, m }: { u: string; s: PlateState; m: FigureMarks }) {
  return (
    <>
      <WholeBodyDefs u={u} s={s} />
      <HeadMask u={u} s={s} m={m} />
      {(s.snared || s.immob) && <CordMask u={u} />}
      {s.mute && m.head && <VoiceBand u={u} head={m.head} />}
      {s.unaware && <CalmBand u={u} />}
      {s.bloodLoss > 0 && <BloodBand u={u} level={s.bloodLoss} />}
    </>
  );
}

// ── The layers ───────────────────────────────────────────────────

export const filterOf = (u: string, on: unknown, id: string) => (on ? `url(#${u}-${id})` : undefined);

/** The states that change how a whole body reads: each one's filter and its share of opacity (per kind). */
export type Looks = [key: keyof PlateState, filter: string, opacity: number][];

/** A figure's: Paralysed icy, Unconscious dim, Helpless dim, Unaware cool and calm. */
const PERSON_LOOKS: Looks = [
  ['paralysed', 'grayscale(1) sepia(.35) hue-rotate(165deg) brightness(1.1)', 1],
  ['unconscious', '', 0.45],
  ['helpless', 'brightness(.8)', 0.6],
  ['unaware', 'saturate(.5) hue-rotate(-28deg) brightness(.95)', 1],
];

/** The states in play as one filter and opacity, on their own layer. */
export function looksStyle(s: PlateState, looks: Looks): CSSProperties | undefined {
  const on = looks.filter(([key]) => s[key]);
  if (on.length === 0) return undefined;
  const filter = on
    .map(([, f]) => f)
    .filter(Boolean)
    .join(' ');
  return { filter: filter || undefined, opacity: on.reduce((a, [, , o]) => a * o, 1) };
}

/** One layer's props: its class, its filter, its style. */
export type Layer = { className?: string; filter?: string; style?: CSSProperties };

/** The layers every kind shares, by the condition they draw. */
export const hwLayer = (u: string, s: PlateState): Layer => ({
  className: clsx(s.haywire === 4 && fx.shutdown),
  filter: filterOf(u, s.haywire, 'hw'),
});
export const hlLayer = (u: string, s: PlateState): Layer => ({
  className: clsx(s.halluc === 6 && fx.lost),
  filter: filterOf(u, s.halluc, 'hl'),
});
export const stunLayer = (u: string, s: PlateState): Layer => ({ filter: filterOf(u, s.stun, 'stun') });
export const heatLayer = (u: string, s: PlateState): Layer => ({ filter: filterOf(u, s.fire, 'heat') });
export const pinLayer = (s: PlateState): Layer => ({ className: clsx(s.pinned && fx.underFire) });

/** Layers nested outside in round what they hold: each condition one `<g>`, so any mix of them stacks. */
export function Stack({ layers, children }: { layers: Layer[]; children: ReactNode }) {
  return layers.reduceRight<ReactNode>((inner, layer) => <g {...layer}>{inner}</g>, children);
}

/**
 * The whole-figure layers round the figure's lines (`children`), outside in, each condition on its own, then the
 * overlays (`overlays`) inside them all, so a stunned or haywired figure carries its overlays with it.
 */
export function ConditionLayers({
  u,
  s,
  children,
  overlays,
}: {
  u: string;
  s: PlateState;
  children: ReactNode;
  overlays?: ReactNode;
}) {
  const deadNow = useArrived(s.dead);
  const outer: Layer[] = [
    { className: clsx(fx.dying, s.dying && fx.on) },
    hwLayer(u, s),
    hlLayer(u, s),
    { style: looksStyle(s, PERSON_LOOKS) },
    { className: clsx(fx.breath, s.fatigue > 0 && fx.pant), style: { '--fat': s.fatigue || 1 } as CSSProperties },
    pinLayer(s),
  ];
  const lines = { className: clsx(s.fire && fx.burning, s.dead && fx.dead, deadNow && fx.deadNow) };
  return (
    <Stack layers={outer}>
      <Stack layers={[stunLayer(u, s), heatLayer(u, s), lines]}>{children}</Stack>
      {overlays}
    </Stack>
  );
}

type LocationLayer = { className?: string; filter?: string; mask?: string; style?: CSSProperties };
type LayerOf = (u: string, s: PlateState, m: FigureMarks) => LocationLayer;

/** The head: Deafened blurs it (pulsing); Blinded, or a deafened helmet's cut, masks it. */
const headLayer: LayerOf = (u, s, m) => ({
  className: clsx(s.deaf && fx.deaf),
  filter: filterOf(u, s.deaf, 'deaf'),
  mask: s.blind || deafCut(s, m) ? `url(#${u}-hm)` : undefined,
});

/** The chest breathes slow and calm from the sternum - unconscious or unaware. */
const bodyLayer: LayerOf = (_u, s, m) =>
  s.unconscious || s.unaware
    ? { className: fx.slowBreath, style: { transformOrigin: `${m.chest[0]}px ${m.chest[1] - 120}px` } }
    : {};

/** A bound leg: cords across it, straining bright (Snared) or still over cold lines (Immobilised). */
const legLayer: LayerOf = (u, s) =>
  s.snared || s.immob ? { className: s.snared ? fx.strain : fx.cold, mask: `url(#${u}-cm)` } : {};

const LOCATION_LAYERS: Record<string, LayerOf> = {
  head: headLayer,
  body: bodyLayer,
  rightLeg: legLayer,
  leftLeg: legLayer,
};

/** A location's own condition layer, between its lines and the figure; nothing for the arms. */
export function locationCondition(u: string, s: PlateState, m: FigureMarks, id: string): LocationLayer {
  return LOCATION_LAYERS[id]?.(u, s, m) ?? {};
}

/** A recoloured copy of the lines, shown through a clip. */
export function Copy({
  u,
  clip,
  colour,
  opacity = 1,
  children,
}: {
  u: string;
  clip: string;
  colour: string;
  opacity?: number;
  children?: ReactNode;
}) {
  return (
    <g
      className={fx.copy}
      clipPath={`url(#${u}-${clip})`}
      style={{ color: `var(${colour})` }}
      opacity={opacity}
      pointerEvents="none"
    >
      {children ?? <use href={`#${u}-art`} fill="currentColor" />}
    </g>
  );
}

/** The overlays: the voice that dies, the calm glow, the blood draining; and a deafened helmet's collar, sharp. */
export function ConditionOverlays({ u, s, m }: { u: string; s: PlateState; m: FigureMarks }) {
  return (
    <>
      {deafCut(s, m) && (
        <g clipPath={`url(#${u}-c-head)`} pointerEvents="none">
          <g className={plate.loc} clipPath={`url(#${u}-collar)`}>
            <use href={`#${u}-art`} fill="currentColor" />
          </g>
        </g>
      )}
      {s.mute && m.head && (
        <Copy u={u} clip="voice" colour="--plate-voice">
          <g clipPath={`url(#${u}-c-head)`}>
            <use href={`#${u}-art`} fill="currentColor" />
          </g>
        </Copy>
      )}
      {s.unaware && <Copy u={u} clip="calm" colour="--plate-calm-glow" opacity={0.55} />}
      {s.bloodLoss > 0 && <Copy u={u} clip="blood" colour="--plate-blood" />}
    </>
  );
}
